import { HttpMethod } from 'dragonmouse-shared/constants';

declare global {
	interface Window {
		// add these properties to the window object so that typescript knows about them
		dataLayer: any[];
		mixpanel: {
			init: (token: string, config?: any) => void;
			track: (eventName: string, properties?: Record<string, any>) => void;
			identify: (userId: string, userProperties?: Record<string, any>) => void;
			get_distinct_id: () => string;
			people: {
				set: (properties: Record<string, any>) => void;
				set_once: (properties: Record<string, any>) => void;
				union: (properties: Record<string, any>) => void;
			};
			reset: () => void;
		};
	}
}

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN || 'e3bca6fd2ae028611e5443f05ce2c43f';

export const initAnalytics = (): void => {
	// Initialize Mixpanel
	if (typeof window !== 'undefined' && typeof window.mixpanel !== 'undefined') {
		window.mixpanel.init(MIXPANEL_TOKEN);
	}
};

export const trackPageView = (url: string, title?: string): void => {
	if (typeof window.mixpanel !== 'undefined') {
		window.mixpanel.track('page_view', {
			page_title: title || document.title,
			page_location: url,
		});
	}
};

const trackEvent = (eventName: string, parameters?: Record<string, any>): void => {
	if (typeof window.mixpanel !== 'undefined') {
		window.mixpanel.track(eventName, {
			source: 'studio',
			...parameters,
		});
	}
};

//--------------------------------
export const trackEndpoint = (method: HttpMethod, route: string): void => {
	trackEvent('endpoint_studio', {
		method,
		route,
	});
};

//--------------------------------
export type SceneAction =
	| 'open'
	| 'open_in_new_tab'
	| 'rename'
	| 'duplicate'
	| 'delete'
	| 'revoke_access'
	| 'grant_access'
	| 'open_share_modal'
	| 'return_to_scene_list';
//----
export const trackSceneAction = (action: SceneAction): void => {
	trackEvent('scene', {
		action,
	});
};

//--------------------------------
export type DesignSystemAction =
	// existing
	| 'open'
	| 'open_in_new_tab'
	| 'rename'
	| 'delete'
	| 'grant_access'
	| 'revoke_access'
	| 'open_share_modal'
	// versioning
	| 'publish'
	| 'revert'
	| 'duplicate'
	| 'restore_version'
	| 'load_old_version'
	| 'download'
	// scene integration
	| 'link_to_scene'
	| 'unlink_from_scene'
	| 'select_existing_ds'
	| 'upgrade_scene_version'
	| 'switch_mode'
	// chat
	| 'send_chat_message'
	| 'toggle_chat_mode'
	| 'new_chat'
	// editor navigation
	| 'navigate_section'
	| 'change_access'
	// picker / browse
	| 'click_add_ds'
	| 'click_add_ds_shortcut_gallery'
	| 'open_gallery'
	// gallery interactions
	| 'gallery_use_ds'
	| 'gallery_view_ds'
	| 'gallery_view_project'
	| 'gallery_setup_ds'
	// CTA page
	| 'view_create_page'
	| 'click_browse_gallery'
	| 'click_start_from_scratch'
	| 'created_blank_ds'
	// creation flows
	| 'create_scene_with_ds'
	// code export
	| 'open_export_ds_code_modal'
	| 'open_export_ds_ai_modal'
	| 'export_ds_code'
	| 'export_ds_ai'
	| 'approve_ds_chat_plan';
//----
export const trackDesignSystemAction = (
	action: DesignSystemAction,
	data?: Record<string, any>
): void => {
	trackEvent('design_system', {
		...data,
		action,
	});
};

//--------------------------------
export type MiscAction =
	| 'undo'
	| 'redo'
	| 'copy_frame'
	| 'delete_frame'
	| 'focus_frame'
	| 'focus_arrow'
	| 'open_frame_share_modal'
	| 'change_scene_background_color'
	| 'change_scene_drop_shadow_mode'
	| 'rename_frame'
	| 'rename_chat'
	| 'double_click_frame_canvas'
	| 'double_click_frame_left_panel'
	| 'load_view_prototype'
	| 'load_edit_prototype'
	| 'load_prototype_in_new_tab'
	| 'toggle_on_preview_mode'
	| 'toggle_on_edit_mode'
	| 'paste_image'
	| 'paste_figma_link'
	| 'paste_frames'
	| 'paste_moonshot'
	| 'open_inspector_side_panel'
	| 'close_inspector_side_panel'
	| 'open_chrome_extension_page'
	| 'rename_ds_chat'
	| 'open_export_frames_assets_modal'
	| 'open_export_frames_code_modal'
	| 'open_export_frames_ai_modal'
	| 'voice_transcribe';
//----
export const trackMiscAction = (action: MiscAction): void => {
	trackEvent('misc', {
		action,
	});
};

//--------------------------------
export type LearningAction =
	| 'open'
	| 'close'
	| 'complete'
	| 'autoplay'
	| 'play'
	| 'pause'
	| 'replay'
	| 'seek'
	| 'next'
	| 'enter_fullscreen';
export type LearningElement = 'onboarding_prompt_input' | 'video_side_panel' | 'video';
//----
export const trackLearningAction = (
	element: LearningElement,
	action: LearningAction,
	id?: string
): void => {
	trackEvent('learn', {
		element,
		action,
		id,
	});
};

//--------------------------------
export type CreativeAction =
	| 'new_scene'
	| 'new_chat'
	| 'new_scene_chat'
	| 'new_ds_chat'
	| 'new_design_system'
	| 'duplicate_scene'
	| 'add_to_ds_chat'
	| 'add_user_entry'
	| 'ds_add_user_entry'
	| 'edit'
	| 'adjust'
	| 'generate'
	| 'generate_from_chat'
	| 'merge'
	| 'prototype'
	| 'retry'
	| 'import_image'
	| 'import_figma'
	| 'import_moonshot'
	| 'duplicate_frame'
	| 'paste_frames_into_scene'
	| 'new_style'
	| 'enhance'
	| 'add_frame_chat_user_entry';
//----
export const trackCreativeAction = (action: CreativeAction): void => {
	trackEvent('create', {
		action,
	});
};

//--------------------------------
export type OutputAction =
	| 'copy_svg'
	| 'copy_svg_elements'
	| 'copy_png'
	| 'copy_html'
	| 'export_svg'
	| 'export_png'
	| 'export_html'
	| 'export_html_with_images'
	| 'export_images'
	| 'export_frames_code'
	| 'export_frames_ai'
	| 'add_comment'
	| 'mention_added'
	| 'open_scene_link'
	| 'open_frame_link'
	| 'copy_scene_link'
	| 'copy_frame_link'
	| 'share_scene_link'
	| 'share_frame_link'
	| 'share_design_system_link'
	| 'copy_design_system_link';
//----
export const trackOutputAction = (action: OutputAction): void => {
	trackEvent('output', {
		action,
	});
};

//--------------------------------
export type VisualEditAction =
	| 'activate_select_tool'
	| 'commit_text_edit'
	| 'commit_image_edit'
	| 'commit_color_edit'
	| 'commit_typography_edit'
	| 'commit_padding_edit'
	| 'commit_offset_edit'
	| 'edit_text_via_panel'
	| 'edit_text_via_dblclick'
	| 'remove_text_edit'
	| 'delete_element';
//----
export const trackVisualEditAction = (action: VisualEditAction): void => {
	trackEvent('visual_edit', {
		action,
	});
};

//--------------------------------
export type EconomyAction =
	| 'upgrade_clicked' // User clicks upgrade button (from modals)
	| 'upgrade_redirected' // User clicks link that redirects to Stripe checkout (from TierCard on profile page)
	| 'manage_subscription_clicked' // User with subscription clicks "Manage Billing" button
	| 'feature_upgrade_modal_shown' // Modal shown when user tries to use feature-gated functionality
	| 'cancel_modal_create_ds_clicked' // Retention modal: user clicked "Set up design system"
	| 'cancel_modal_import_url_clicked' // Retention modal: user hit Go on URL import
	| 'cancel_modal_discord_clicked' // Retention modal: user clicked Discord row
	| 'cancel_modal_kept_subscription' // Retention modal: user clicked Keep Subscription
	| 'cancel_modal_confirmed_cancel'; // Retention modal: user clicked Cancel Subscription (continues to Stripe)
//----
export const trackEconomyAction = (action: EconomyAction): void => {
	trackEvent('economy', {
		action,
	});
};

//--------------------------------
export const trackReferralLinkCopied = (): void => {
	trackEvent('Referral Link Copied');
};

//--------------------------------
// GTM events
//--------------------------------
export const sendGTMEvent = (eventName: string, parameters?: Record<string, any>): void => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: eventName,
		...parameters,
	});
};
